import 'Core-utils/helpers/rulesAgGridModules';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AgGridBase } from 'Components/organisms/AgGridBase';
import { Typography } from 'Components/atoms';
import { RiErrorWarningFill } from '@react-icons/all-files/ri/RiErrorWarningFill';
import { MdOutlineError } from '@react-icons/all-files/md/MdOutlineError';
import {
    CONFIGURE_FINDINGS_SCORE_LABELS,
    ERROR_MESSAGE,
    ERROR_SCORE_TEXT,
    HEALTH_SCORE_CONFIGURATION_ERROR_MESSAGE,
    POINTS,
} from 'Core-utils/constants/constants';
import { Image } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import { updateFindings, getFindingSpecs } from 'Apis/library';
import { FindingSpecDto, FindingSpecKind, KIND } from '@ampsec/platform-client';
import { useProviderData } from 'Hooks/useProviderData';
import AmplifierLogo from 'Assets/icons/AmpSec.svg';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { CustomTooltip } from 'Components/atoms';
import { ToastMessage } from 'Components/molecules';
import { useI18n } from 'Hooks/useI18n';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import './styles.scss';

const FINDING_SPEC_EXCLUDE_PRESET_HEALTH_SCORE_CONFIGURATION = 'healthScoreConfiguration';

const FINDING_SPECS_FILTER = {
    'sort[score]': 'DESC',
    'sort[displayValue]': 'ASC',
    excludePreset: FINDING_SPEC_EXCLUDE_PRESET_HEALTH_SCORE_CONFIGURATION,
    limit: 250,
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

interface HealthScoreGridContext {
    connectorProviderMap: any[];
    formatDisplayLabel: (value: string) => string;
    t: (key: string, fallback?: string) => string;
    addToast: (toast: any) => void;
}

// ─── Cell renderers ──────────────────────────────────────────────────────────

const FindingCellRenderer = ({ data, context }: ICellRendererParams<FindingSpecDto>) => {
    const { connectorProviderMap, formatDisplayLabel } = context as HealthScoreGridContext;
    const finding = connectorProviderMap.find((item) => item.cid === data?.cid);
    const providerIcon = data?.cid === null ? AmplifierLogo : finding?.providerIcon;
    const displayIcon = providerIcon ? (
        <Image src={providerIcon} width={14} height={17} />
    ) : (
        <MdOutlineError className="text-secondary-accent-red-2" size={20} />
    );
    return (
        <div className="d-flex flex-row gap-2 align-items-center">
            {displayIcon}
            <Typography variant="body10" color="text-high-emphasis" style={{ marginBottom: 0 }}>
                {formatDisplayLabel(data?.displayValue ?? '')}
            </Typography>
        </div>
    );
};

const ScoreCellRenderer = ({ data, context }: ICellRendererParams<FindingSpecDto>) => {
    const { t, addToast } = context as HealthScoreGridContext;
    const isSystemFinding = data?.kind === FindingSpecKind.SYSTEM;
    const [formControlValue, setFormControlValue] = useState<number | null>(data?.score ?? 0);
    const [isValidInput, setIsValidInput] = useState<boolean>(true);
    const [initialLoad, setInitialLoad] = useState<boolean>(true);

    const getScoreUpdateErrorMessage = (error: any): string => {
        const statusCode = error?.response?.status || error?.status;
        const errorMessage = error?.response?.data?.message || error?.data?.message || error?.message;
        if (statusCode === 400 && errorMessage) {
            return t(HEALTH_SCORE_CONFIGURATION_ERROR_MESSAGE);
        }
        return t(ERROR_MESSAGE);
    };

    const handleFormControlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isSystemFinding) {
            return;
        }
        const intValue = parseInt(e.target.value, 10);
        setFormControlValue(isNaN(intValue) ? null : intValue);
    };

    useEffect(() => {
        const updateCustomScoreValues = () => {
            if (!initialLoad && isValidInput && !isSystemFinding && data) {
                updateFindings({
                    ...data,
                    score: formControlValue ?? 0,
                }).catch((error) => {
                    addToast({ severity: 'error', body: getScoreUpdateErrorMessage(error) });
                });
            }
        };
        const timer = setTimeout(updateCustomScoreValues, 500);
        return () => clearTimeout(timer);
    }, [formControlValue, isValidInput]);

    useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
        }
        setIsValidInput((formControlValue ?? 0) >= 0 && (formControlValue ?? 0) < 100);
    }, [formControlValue]);

    const inputValueColor = isValidInput ? 'text-high-emphasis' : 'secondary-accent-red-2';

    return (
        <div className="d-flex gap-2 align-items-center">
            <Form.Control
                size="sm"
                className={`settings-page__form bg-structural-BG border-0 shadow-none text-${inputValueColor} ${
                    isSystemFinding ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                value={formControlValue ?? 0}
                onChange={handleFormControlChange}
                disabled={isSystemFinding}
                aria-label={data?.displayValue ?? ''}
            />
            <Typography variant="body6" color="text-low-emphasis" style={{ marginBottom: '0%' }}>
                {POINTS}
            </Typography>
            {isSystemFinding && (
                <CustomTooltip label={t(HEALTH_SCORE_CONFIGURATION_ERROR_MESSAGE)}>
                    <RiErrorWarningFill className="text-text-low-emphasis" size={20} />
                </CustomTooltip>
            )}
            {!isSystemFinding && !isValidInput && (
                <CustomTooltip label={ERROR_SCORE_TEXT} labelColor="secondary-accent-red-2">
                    <RiErrorWarningFill className="text-secondary-accent-red-2" size={20} />
                </CustomTooltip>
            )}
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const HealthScoreConfigurationTable = () => {
    const { addToast } = useContext(ToastsContext);
    const { connectorProviderMap } = useProviderData();
    const { t, formatDisplayLabel } = useI18n();

    const { data: findingSpecs } = useApiObjectData<FindingSpecDto[], FindingSpecDto[]>(
        KIND.FINDING_SPECS,
        getFindingSpecs,
        (a) => a,
        FINDING_SPECS_FILTER,
    );

    const safeFindingSpecs = Array.isArray(findingSpecs) ? findingSpecs : [];

    const columnDefs: ColDef<FindingSpecDto>[] = useMemo(
        () => [
            {
                colId: 'findings',
                headerName: CONFIGURE_FINDINGS_SCORE_LABELS[0],
                sortable: false,
                flex: 1,
                cellRenderer: FindingCellRenderer,
            },
            {
                colId: 'score',
                headerName: CONFIGURE_FINDINGS_SCORE_LABELS[1],
                sortable: false,
                width: 200,
                cellRenderer: ScoreCellRenderer,
            },
        ],
        [],
    );

    const gridContext: HealthScoreGridContext = useMemo(
        () => ({ connectorProviderMap, formatDisplayLabel, t, addToast }),
        [connectorProviderMap, formatDisplayLabel, t, addToast],
    );

    return (
        <div className="d-flex flex-column gap-2">
            <ToastMessage toastSubText={t('CUSTOM_SCORE_HELP')} variant="info" />
            <AgGridBase.Root fullWidth>
                <AgGridBase.Table<FindingSpecDto>
                    columnDefs={columnDefs}
                    dataMode="client"
                    rowData={safeFindingSpecs}
                    bodyHeight="32rem"
                    wrapperClassName="ag-theme-alpine ag-findings-grid__wrapper ag-findings-grid__wrapper--theme-findings ag-grid-theme--amp-dark health-score-grid"
                    movableColumns={false}
                    agGridProps={{
                        context: gridContext,
                        getRowId: (params) => String(params.data?.id ?? ''),
                        suppressContextMenu: true,
                    }}
                />
            </AgGridBase.Root>
        </div>
    );
};

export default HealthScoreConfigurationTable;
